import { create } from "zustand";
import {
  ComplianceCertificate,
  ComplianceChecklist,
  ComplianceChecklistConfig,
} from "../types/api";
import {
  getComplianceCertificates,
  createComplianceCertificate,
  updateComplianceCertificate,
  getComplianceChecklists,
  createComplianceChecklist,
  updateComplianceChecklist,
  deleteComplianceCertificate,
  deleteComplianceChecklist,
  generateComplianceChecklists,
  getComplianceChecklistConfigs,
  createComplianceChecklistConfig,
  deleteComplianceChecklistConfig,
} from "../service/api";

interface ComplianceState {
  certificates: ComplianceCertificate[];
  checklists: ComplianceChecklist[];
  configs: ComplianceChecklistConfig[];
  loading: boolean;
  error: string | null;

  fetchCertificates: () => Promise<void>;
  addCertificate: (cert: Omit<ComplianceCertificate, "id" | "status">) => Promise<boolean>;
  updateCertificate: (id: string, cert: Partial<ComplianceCertificate>) => Promise<boolean>;
  deleteCertificate: (id: string) => Promise<boolean>;
  
  fetchChecklists: () => Promise<void>;
  addChecklist: (checklist: Omit<ComplianceChecklist, "id" | "status">) => Promise<boolean>;
  updateChecklistStatus: (id: string, status: ComplianceChecklist["status"]) => Promise<boolean>;
  updateChecklist: (id: string, data: Partial<ComplianceChecklist>) => Promise<boolean>;
  deleteChecklist: (id: string) => Promise<boolean>;
  generateChecklists: () => Promise<void>;

  fetchConfigs: () => Promise<void>;
  addConfig: (config: Omit<ComplianceChecklistConfig, "id">) => Promise<boolean>;
  deleteConfig: (id: string) => Promise<boolean>;
}

export const useComplianceStore = create<ComplianceState>((set, get) => ({
  certificates: [],
  checklists: [],
  configs: [],
  loading: false,
  error: null,

  fetchCertificates: async () => {
    set({ loading: true, error: null });
    try {
      const res = await getComplianceCertificates();
      if (res.data.success && res.data.data) {
        set({ certificates: res.data.data, loading: false });
      } else {
        set({ error: "Failed to fetch certificates", loading: false });
      }
    } catch (err: any) {
      set({ error: err.message || "Failed to fetch certificates", loading: false });
    }
  },

  addCertificate: async (certData) => {
    set({ loading: true, error: null });
    try {
      const res = await createComplianceCertificate(certData);
      if (res.data.success && res.data.data) {
        set((state) => ({
          certificates: [res.data.data!, ...state.certificates],
          loading: false,
        }));
        return true;
      }
      set({ error: "Failed to add certificate", loading: false });
      return false;
    } catch (err: any) {
      set({ error: err.message || "Failed to add certificate", loading: false });
      return false;
    }
  },

  updateCertificate: async (id, certData) => {
    set({ loading: true, error: null });
    try {
      const res = await updateComplianceCertificate(id, certData);
      if (res.data.success && res.data.data) {
        set((state) => ({
          certificates: state.certificates.map(c => c.id === id ? res.data.data! : c),
          loading: false,
        }));
        return true;
      }
      set({ error: "Failed to update certificate", loading: false });
      return false;
    } catch (err: any) {
      set({ error: err.message || "Failed to update certificate", loading: false });
      return false;
    }
  },

  deleteCertificate: async (id) => {
    set({ loading: true, error: null });
    try {
      const res = await deleteComplianceCertificate(id);
      if (res.data.success) {
        set((state) => ({
          certificates: state.certificates.filter((c) => c.id !== id),
          loading: false,
        }));
        return true;
      }
      set({ error: "Failed to delete certificate", loading: false });
      return false;
    } catch (err: any) {
      set({ error: err.message || "Failed to delete certificate", loading: false });
      return false;
    }
  },

  fetchChecklists: async () => {
    set({ loading: true, error: null });
    try {
      const res = await getComplianceChecklists();
      if (res.data.success && res.data.data) {
        set({ checklists: res.data.data, loading: false });
      } else {
        set({ error: "Failed to fetch checklists", loading: false });
      }
    } catch (err: any) {
      set({ error: err.message || "Failed to fetch checklists", loading: false });
    }
  },

  addChecklist: async (checklistData) => {
    set({ loading: true, error: null });
    try {
      const res = await createComplianceChecklist(checklistData);
      if (res.data.success && res.data.data) {
        set((state) => ({
          checklists: [...state.checklists, res.data.data!],
          loading: false,
        }));
        return true;
      }
      set({ error: "Failed to add checklist", loading: false });
      return false;
    } catch (err: any) {
      set({ error: err.message || "Failed to add checklist", loading: false });
      return false;
    }
  },

  updateChecklistStatus: async (id, status) => {
    // Optimistic update
    const originalChecklists = get().checklists;
    set((state) => ({
      checklists: state.checklists.map((c) =>
        c.id === id ? { ...c, status } : c
      ),
    }));

    try {
      const res = await updateComplianceChecklist(id, { status });
      if (!res.data.success) {
        // Revert on failure
        set({ checklists: originalChecklists, error: "Failed to update status" });
        return false;
      }
      return true;
    } catch (err: any) {
      set({ checklists: originalChecklists, error: err.message || "Failed to update status" });
      return false;
    }
  },

  deleteChecklist: async (id) => {
    set({ loading: true, error: null });
    try {
      const res = await deleteComplianceChecklist(id);
      if (res.data.success) {
         set((state) => ({
          checklists: state.checklists.filter((c) => c.id !== id),
          loading: false,
        }));
        return true;
      }
       set({ error: "Failed to delete checklist", loading: false });
       return false;
    } catch (err: any) {
      set({ error: err.message || "Failed to delete checklist", loading: false });
      return false;
    }
  },

  generateChecklists: async () => {
    set({ loading: true, error: null });
    try {
      const res = await generateComplianceChecklists();
      if (res.data.success && res.data.data) {
        set({ checklists: res.data.data, loading: false });
      } else {
        set({ error: "Failed to generate checklists", loading: false });
      }
    } catch (err: any) {
      set({ error: err.message || "Failed to generate checklists", loading: false });
    }
  },

  fetchConfigs: async () => {
    set({ loading: true, error: null });
    try {
      const res = await getComplianceChecklistConfigs();
      if (res.data.success && res.data.data) {
        set({ configs: res.data.data, loading: false });
      } else {
        set({ error: "Failed to fetch configs", loading: false });
      }
    } catch (err: any) {
      set({ error: err.message || "Failed to fetch configs", loading: false });
    }
  },

  addConfig: async (configData) => {
    set({ loading: true, error: null });
    try {
      const res = await createComplianceChecklistConfig(configData);
      if (res.data.success && res.data.data) {
        set((state) => ({
          configs: [res.data.data!, ...state.configs],
          loading: false,
        }));
        return true;
      }
      set({ error: "Failed to add config", loading: false });
      return false;
    } catch (err: any) {
      set({ error: err.message || "Failed to add config", loading: false });
      return false;
    }
  },

  updateChecklist: async (id, data) => {
    // Optimistic update
    const originalChecklists = get().checklists;
    set((state) => ({
      checklists: state.checklists.map((c) =>
        c.id === id ? { ...c, ...data } : c
      ),
    }));

    try {
      const res = await updateComplianceChecklist(id, data);
      if (!res.data.success) {
        // Revert on failure
        set({ checklists: originalChecklists, error: "Failed to update checklist" });
        return false;
      }
      return true;
    } catch (err: any) {
      set({ checklists: originalChecklists, error: err.message || "Failed to update checklist" });
      return false;
    }
  },

  deleteConfig: async (id) => {
     set({ loading: true, error: null });
    try {
      const res = await deleteComplianceChecklistConfig(id);
      if (res.data.success) {
         set((state) => ({
          configs: state.configs.filter((c) => c.id !== id),
          loading: false,
        }));
        return true;
      }
       set({ error: "Failed to delete config", loading: false });
       return false;
    } catch (err: any) {
      set({ error: err.message || "Failed to delete config", loading: false });
      return false;
    }
  }
}));
